import { PAYOUT_API_CONFIGS } from './api-explorer.payout.config';

const ERROR_RESPONSE_FIELDS = [
  { name: 'status', type: 'integer', description: 'HTTP status code' },
  { name: 'error', type: 'string', description: 'Error type' },
  { name: 'message', type: 'string', description: 'Detailed error message' },
  { name: 'timestamp', type: 'string', description: 'Time of the error' },
  { name: 'path', type: 'string', description: 'Request path that caused the error' }
];

export const API_PAGE_CONFIGS: Record<string, any> = {

  'token': {
    title: 'Generate access token',
    version: '1.0',
    serverUrl: '',
    schemas: {},
    endpoints: [
      {
        isFromPG: false,
        path: '/auth/api-credential/token',
        method: 'POST',
        tag: 'Authentication',
        title: 'Generate access token',
        description: 'Authenticate with client credentials and receive JWT access and refresh tokens.',
        operationId: 'generateToken',
        parameters: [],
        requestBody: null,
        requestBodyFields: [
          { name: 'clientId', required: true, description: 'Client ID issued for your API credential', schema: { type: 'string', example: 'ff6d4744-19ae-4935-9bce-6190094e331e' } },
          { name: 'clientSecret', required: true, description: 'Client secret issued for your API credential', schema: { type: 'string', example: '4920d851be0f44029f937148a42723368b54fd3297f44c7aaee502abf4979667' } }
        ],
        responses: [
          {
            status: '200',
            description: 'Token generated successfully',
            content: {
              accessToken: 'eyJhbGciOiJIUzI1NiJ9.<payload>.<signature>',
              refreshToken: 'eyJhbGciOiJIUzI1NiJ9.<payload>.<signature>',
              accessTokenExpiresIn: 600,
              refreshTokenExpiresIn: 604800,
              tokenType: 'Bearer'
            },
            responseFields: [
              { name: 'accessToken', type: 'string', description: 'JWT access token' },
              { name: 'refreshToken', type: 'string', description: 'JWT refresh token' },
              { name: 'accessTokenExpiresIn', type: 'integer', description: 'Access token expiry in seconds' },
              { name: 'refreshTokenExpiresIn', type: 'integer', description: 'Refresh token expiry in seconds' },
              { name: 'tokenType', type: 'string', description: 'Token type' }
            ]
          },
          {
            status: '400',
            description: 'Bad Request — validation failures',
            content: { timestamp: '2026-05-26T11:55:00.123+00:00', status: 400, error: 'Bad Request', path: '/auth/api-credential/token' },
            responseFields: [
              { name: 'timestamp', type: 'string', description: 'Time of the error' },
              { name: 'status', type: 'integer', description: 'HTTP status code' },
              { name: 'error', type: 'string', description: 'Error type' },
              { name: 'path', type: 'string', description: 'Request path that caused the error' }
            ]
          },
          {
            status: '401',
            description: 'Unauthorized — invalid client credentials',
            content: { timestamp: '2026-05-26 11:55:00.123', status: 401, httpStatus: 'UNAUTHORIZED', data: null, message: 'Invalid client credentials' },
            responseFields: [
              { name: 'timestamp', type: 'string', description: 'Time of the error' },
              { name: 'status', type: 'integer', description: 'HTTP status code' },
              { name: 'httpStatus', type: 'string', description: 'HTTP status text' },
              { name: 'data', type: 'object', description: 'Response data (null on error)' },
              { name: 'message', type: 'string', description: 'Error message' }
            ]
          }
        ],
        successResponse: {
          status: '200',
          description: 'Token generated successfully',
          contentType: 'JSON',
          content: {
            accessToken: 'eyJhbGciOiJIUzI1NiJ9.<payload>.<signature>',
            refreshToken: 'eyJhbGciOiJIUzI1NiJ9.<payload>.<signature>',
            accessTokenExpiresIn: 600,
            refreshTokenExpiresIn: 604800,
            tokenType: 'Bearer'
          }
        },
        failureResponse: {
          status: '401',
          description: 'Unauthorized — invalid client credentials',
          contentType: 'JSON',
          content: { timestamp: '2026-05-26 11:55:00.123', status: 401, httpStatus: 'UNAUTHORIZED', data: null, message: 'Invalid client credentials' }
        }
      }
    ]
  },

  'refresh': {
    title: 'Generate Refresh Token',
    version: '1.0',
    serverUrl: '',
    schemas: {},
    endpoints: [
      {
        isFromPG: false,
        path: '/auth/api-credential/refresh',
        method: 'POST',
        tag: 'Authentication',
        title: 'Generate Refresh Token',
        description: 'Exchange a valid refresh token for a new access token and a rotated refresh token. Each refresh token is single-use; a new one is returned on every successful call.',
        operationId: 'refreshToken',
        parameters: [
          { name: 'x-refresh-token', in: 'header', required: true, description: 'Valid refresh token issued from a previous authentication or token refresh', schema: { type: 'string', example: '<YOUR_REFRESH_TOKEN_HERE>' } }
        ],
        requestBody: null,
        requestBodyFields: [],
        responses: [
          {
            status: '200',
            description: 'Token rotated successfully — new access token and rotated refresh token returned',
            content: {
              accessToken: 'eyJhbGciOiJIUzI1NiJ9.<payload>.<signature>',
              refreshToken: 'eyJhbGciOiJIUzI1NiJ9.<payload>.<signature>',
              accessTokenExpiresIn: 600,
              refreshTokenExpiresIn: 604800,
              tokenType: 'Bearer'
            },
            responseFields: [
              { name: 'accessToken', type: 'string', description: 'New JWT access token' },
              { name: 'refreshToken', type: 'string', description: 'Rotated JWT refresh token (single-use)' },
              { name: 'accessTokenExpiresIn', type: 'integer', description: 'Access token expiry in seconds' },
              { name: 'refreshTokenExpiresIn', type: 'integer', description: 'Refresh token expiry in seconds' },
              { name: 'tokenType', type: 'string', description: 'Token type' }
            ]
          },
          {
            status: '400',
            description: 'Bad Request — blank or missing x-refresh-token header value',
            content: { timestamp: '2026-05-26 12:52:00.123', status: 400, httpStatus: 'BAD_REQUEST', data: null, message: 'Refresh token is required' },
            responseFields: [
              { name: 'timestamp', type: 'string', description: 'Time of the error' },
              { name: 'status', type: 'integer', description: 'HTTP status code' },
              { name: 'httpStatus', type: 'string', description: 'HTTP status text' },
              { name: 'data', type: 'object', description: 'Response data (null on error)' },
              { name: 'message', type: 'string', description: 'Error message' }
            ]
          },
          {
            status: '401',
            description: 'Unauthorized — possible causes: token expired, invalid/malformed token, wrong token type, client revoked, reuse detected, jti mismatch, or credential no longer active',
            content: { timestamp: '2026-05-26 12:52:00.123', status: 401, httpStatus: 'UNAUTHORIZED', data: null, message: 'Invalid refresh token' },
            responseFields: [
              { name: 'timestamp', type: 'string', description: 'Time of the error' },
              { name: 'status', type: 'integer', description: 'HTTP status code' },
              { name: 'httpStatus', type: 'string', description: 'HTTP status text' },
              { name: 'data', type: 'object', description: 'Response data (null on error)' },
              { name: 'message', type: 'string', description: 'Specific error reason (see description)' }
            ]
          },
          {
            status: '500',
            description: 'Internal Server Error — infrastructure failure (Redis, DB, or JWT signing). Returns Spring default error body.',
            content: { timestamp: '2026-05-26T12:52:00.123+00:00', status: 500, error: 'Internal Server Error', path: '/auth/api-credential/refresh' },
            responseFields: [
              { name: 'timestamp', type: 'string', description: 'Time of the error' },
              { name: 'status', type: 'integer', description: 'HTTP status code' },
              { name: 'error', type: 'string', description: 'Error type' },
              { name: 'path', type: 'string', description: 'Request path that caused the error' }
            ]
          }
        ],
        successResponse: {
          status: '200',
          description: 'Token rotated successfully',
          contentType: 'JSON',
          content: {
            accessToken: 'eyJhbGciOiJIUzI1NiJ9.<payload>.<signature>',
            refreshToken: 'eyJhbGciOiJIUzI1NiJ9.<payload>.<signature>',
            accessTokenExpiresIn: 600,
            refreshTokenExpiresIn: 604800,
            tokenType: 'Bearer'
          }
        },
        failureResponse: {
          status: '401',
          description: 'Unauthorized — invalid refresh token',
          contentType: 'JSON',
          content: { timestamp: '2026-05-26 12:52:00.123', status: 401, httpStatus: 'UNAUTHORIZED', data: null, message: 'Invalid refresh token' }
        }
      }
    ]
  },

  '_generic': {
    title: 'Generic',
    version: '1.0',
    serverUrl: '',
    schemas: {},
    endpoints: [
      {
        path: '/phone/{countryCode}/{phoneNumber}/validate',
        method: 'POST',
        tag: 'Phone',
        title: 'Generic',
        description: 'Validate a phone number and return its country, format, and carrier details.',
        operationId: 'validatePhoneNumber',
        parameters: [
          { name: 'countryCode', in: 'path', required: true, description: 'ISO 3166-1 alpha-2 country code', schema: { type: 'string', example: 'IN' } },
          { name: 'phoneNumber', in: 'path', required: true, description: 'Phone number in E.164 format', schema: { type: 'string', example: '+919876543210' } },
          { name: 'Authorization', in: 'header', required: true, description: 'Bearer access token', schema: { type: 'string', example: 'Bearer eyJhbGciOiJIUzI1NiJ9' } },
          { name: 'X-Request-Id', in: 'header', required: true, description: 'Unique request tracking id', schema: { type: 'string', example: 'REQ-20001' } },
          { name: 'format', in: 'query', required: true, description: 'Response format for the phone number', schema: { type: 'string', example: 'E164' } },
          { name: 'includeCarrier', in: 'query', required: false, description: 'Whether to include carrier details', schema: { type: 'string', enum: ['true', 'false'], example: 'true' } }
        ],
        requestBody: null,
        requestBodyFields: [
          { name: 'phoneNumber', required: true, description: 'Phone number to validate', schema: { type: 'string', example: '9876543210' } },
          { name: 'outputFormat', required: false, description: 'Desired format for the returned phone number', schema: { type: 'string', example: 'E164', enum: ['E164', 'NATIONAL', 'INTERNATIONAL', 'RFC3966'] } },
          { name: 'retryCount', required: false, description: 'Number of retry attempts on failure', schema: { type: 'integer', example: 3 } }
        ],
        responses: [
          {
            status: '200',
            description: 'Phone number validated successfully',
            content: { valid: true, phoneNumber: '+919876543210', countryCode: 'IN', countryName: 'India', carrier: 'Jio', lineType: 'mobile' },
            responseFields: [
              { name: 'valid', type: 'boolean', description: 'Whether the phone number is valid' },
              { name: 'phoneNumber', type: 'string', description: 'Normalized E.164 formatted phone number' },
              { name: 'countryCode', type: 'string', description: 'Detected country code' },
              { name: 'countryName', type: 'string', description: 'Detected country name' },
              { name: 'carrier', type: 'string', description: 'Carrier name associated with the number' },
              { name: 'lineType', type: 'string', description: 'Type of phone line' }
            ]
          },
          {
            status: '400',
            description: 'Invalid request payload',
            content: { status: 400, error: 'Bad Request', message: 'phoneNumber is required', timestamp: '2024-05-18T10:30:00Z', path: '/phone/validate' },
            responseFields: ERROR_RESPONSE_FIELDS
          },
          {
            status: '401',
            description: 'Unauthorized request',
            content: { status: 401, error: 'Unauthorized', message: 'phoneNumber is required', timestamp: '2024-05-18T10:30:00Z', path: '/phone/validate' },
            responseFields: ERROR_RESPONSE_FIELDS
          },
          {
            status: '500',
            description: 'Internal server error',
            content: { status: 500, error: 'Internal Server Error', message: 'An unexpected error occurred', timestamp: '2024-05-18T10:30:00Z', path: '/phone/validate' },
            responseFields: ERROR_RESPONSE_FIELDS
          }
        ],
        successResponse: {
          status: '200',
          description: 'Phone number validated successfully',
          contentType: 'JSON',
          content: { valid: true, phoneNumber: '+919876543210', countryCode: 'IN', countryName: 'India', carrier: 'Jio', lineType: 'mobile' }
        },
        failureResponse: {
          status: '400',
          description: 'Invalid request payload',
          contentType: 'JSON',
          content: { status: 400, error: 'Bad Request', message: 'phoneNumber is required', timestamp: '2024-05-18T10:30:00Z', path: '/phone/validate' }
        }
      }
    ]
  },

  'validate-phonenumber': {
    title: 'Validate Mobile Number',
    version: '1.0',
    serverUrl: '',
    schemas: {},
    endpoints: [
      {
        isFromPG: true,
        path: '/pgws/wallet/validate',
        method: 'POST',
        tag: 'Payment Gateway',
        title: 'Validate Mobile Number',
        description: 'Validate whether a customer\'s mobile number has an existing Pay10 wallet account. The service builds the encrypted request, calls Pay10 PG /pgws/wallet/validate, and returns the decrypted response as a flat key-value object.',
        operationId: 'validateMobile',
        parameters: [
          // { name: 'Authorization', in: 'header', required: true, description: 'Bearer access token', schema: { type: 'string', example: 'Bearer eyJhbGciOiJIUzI1NiJ9' } }
        ],
        requestBody: null,
        requestBodyFields: [
          { name: 'PAY_ID', required: true, fixed: true, description: 'Unique identifier provided by Pay10', schema: { type: 'string', example: '312432' } },
          { name: 'ORDER_ID', required: true, description: 'Merchant reference number. Auto-generated as ORD<epochMillis> if omitted.', schema: { type: 'string', example: 'ORD1748340000000' } },
          { name: 'AMOUNT', required: true, description: 'Transaction amount in fils (100 AED = 10000 fils)', schema: { type: 'string', example: '10000' } },
          { name: 'CURRENCY_CODE', required: true,fixed: true, description: 'ISO 4217 numeric currency code (e.g. "784" for AED, "356" for INR)', schema: { type: 'string', example: '784' } },
          { name: 'TXNTYPE', required: true, fixed: true, description: 'Transaction type ', schema: { type: 'string', example: 'SALE' } },
          { name: 'MOP_TYPE', required: true, fixed: true, description: 'Mode of Payment ', schema: { type: 'string', example: 'PAYQR' } },
          { name: 'PAYMENT_TYPE', required: true, fixed: true, description: 'Payment type ', schema: { type: 'string', example: 'PTQR' } },
          { name: 'CUST_EMAIL', required: true, description: 'Customer email address (valid format)', schema: { type: 'string', example: 'test@gmail.com' } },
          { name: 'CUST_PHONE', required: true, description: 'Customer phone number with country code', schema: { type: 'string', example: '971501234567' } },
          { name: 'PAYMENTS_REGION', required: true, fixed: true, description: 'Region of payment', schema: { type: 'string', example: 'DOMESTIC' } },
          { name: 'CARD_HOLDER_TYPE', required: true, fixed: true, description: 'Type of card holder', schema: { type: 'string', example: 'CONSUMER' } },
          { name: 'PAYER_ADDRESS', required: false, description: 'Mobile number of the payer. ', schema: { type: 'string', example: '1234567890' } }
        ],
        responses: [
          {
            status: '200',
            description: 'Validate a mobile number against Pay10 wallet',
            content: {
              RESPONSE_CODE: '788',
              PAYER_NAME: 'NA',
              TXN_ID: '2520125442672117',
              CUST_PHONE: '123456789',
              MOP_TYPE: 'PAYQR',
              WALLET_CUST_NAME: 'JON DOE',
              TXNTYPE: 'SALE',
              CURRENCY_CODE: '784',
              HASH: 'EEB2FA1D31E0E109775E61A44C0FA3B249FEE897FC6350C791D5F5DA3235797D',
              PAYMENT_TYPE: 'PTQR',
              PG_TXN_MESSAGE: 'Wallet already exists',
              STATUS: 'Success',
              PAY_ID: '6930751900',
              PAYER_ADDRESS: '501167154',
              ORDER_ID: '20250620_165442',
              CUST_EMAIL: 'user@example.com'
            },
            responseFields: [
              { name: 'RESPONSE_CODE', type: 'string', description: 'Pay10 response code — 788 indicates a valid mobile number for this API (unlike other endpoints where 000 & 026 indicates success)' },
              { name: 'PAYER_NAME', type: 'string', description: 'Payer name (NA if not available)' },
              { name: 'TXN_ID', type: 'string', description: 'Transaction ID generated by Pay10' },
              { name: 'CUST_PHONE', type: 'string', description: 'User phone number echoed from the request' },
              { name: 'MOP_TYPE', type: 'string', description: 'Mode of payment (PAYQR)' },
              { name: 'WALLET_CUST_NAME', type: 'string', description: 'Customer name registered with the Pay10 wallet' },
              { name: 'TXNTYPE', type: 'string', description: 'Transaction type (SALE)' },
              { name: 'CURRENCY_CODE', type: 'string', description: 'ISO numeric currency code (784 = AED)' },
              { name: 'HASH', type: 'string', description: 'Generated hash of the parameters for integrity verification' },
              { name: 'PAYMENT_TYPE', type: 'string', description: 'Payment sub-type (PTQR)' },
              { name: 'PG_TXN_MESSAGE', type: 'string', description: 'Response message from the payment gateway' },
              { name: 'STATUS', type: 'string', description: 'Transaction status (e.g. Success)' },
              { name: 'PAY_ID', type: 'string', description: 'Merchant ID provided by Pay10' },
              { name: 'PAYER_ADDRESS', type: 'string', description: 'Payer mobile number' },
              { name: 'ORDER_ID', type: 'string', description: 'Unique order ID echoed from the request' },
              { name: 'CUST_EMAIL', type: 'string', description: 'Customer email ID echoed from the request' }
            ]
          }
        ],
        successResponse: {
          status: '200',
          description: 'Wallet lookup succeeded',
          contentType: 'JSON',
          content: {
            RESPONSE_CODE: '788',
            PAYER_NAME: 'NA',
            TXN_ID: '2520125442672117',
            CUST_PHONE: '123456789',
            MOP_TYPE: 'PAYQR',
            WALLET_CUST_NAME: 'JON DOE',
            TXNTYPE: 'SALE',
            CURRENCY_CODE: '784',
            HASH: 'EEB2FA1D31E0E109775E61A44C0FA3B249FEE897FC6350C791D5F5DA3235797D',
            PAYMENT_TYPE: 'PTQR',
            PG_TXN_MESSAGE: 'Wallet already exists',
            STATUS: 'Success',
            PAY_ID: '6930751900',
            PAYER_ADDRESS: '501167154',
            ORDER_ID: '20250620_165442',
            CUST_EMAIL: 'user@example.com'
          }
        },
        failureResponse: {
          status: '400',
          description: 'Validation error',
          contentType: 'JSON',
          content: {
            RESPONSE_CODE: '374',
            PAYER_NAME: 'NA',
            TXN_ID: '2624173202726037',
            CUST_PHONE: '768355253',
            MOP_TYPE: 'PAYQR',
            TXNTYPE: 'SALE',
            CURRENCY_CODE: '784',
            HASH: '257247036CD663C45BEA434B159AB9C96607D83117DBA2553962BB4147A23D9F',
            PAYMENT_TYPE: 'PTQR',
            PG_TXN_MESSAGE: 'Invalid wallet',
            STATUS: 'Invalid',
            PAY_ID: '66220289',
            PAYER_ADDRESS: '511111112',
            ORDER_ID: '20260724_173202',
            CUST_EMAIL: 'ckkadam1999@gmail.com'
          }
        }
      }
    ]
  },

  'request-to-pay': {
    title: 'Generate Dynamic QR / Request to Pay',
    version: '1.0',
    serverUrl: '',
    schemas: {},
    endpoints: [
      {
        isFromPG: true,
        // path: '/developer-portal/payment-gateway/collect-pay',
        path: '/pgws/wallet/collectpay',
        method: 'POST',
        tag: 'Payment Gateway',
        title: 'Generate Dynamic QR / Request to Pay',
        description: 'Initiates a payment via Dynamic QR (no PAYER_ADDRESS) or Request to Pay (with PAYER_ADDRESS). For QR, a scannable code is generated for the Pay10 app. For RTP, a push notification is sent to the payer for approval. All field values are strings unless stated; fixed-value fields must be passed as specified. Channel Partner must provide IPs for Pay10 whitelisting.',
        parameters: [
          // { name: 'Authorization', in: 'header', required: true, description: 'Bearer access token', schema: { type: 'string', example: 'Bearer eyJhbGciOiJIUzI1NiJ9' } }
        ],
        requestBody: null,
        requestBodyFields: [
          { name: 'PAY_ID', required: true, fixed: true, description: 'Unique identifier provided by Pay10', schema: { type: 'string', example: '312432' } },
          { name: 'ORDER_ID', required: true, description: 'Merchant reference number. Auto-generated as ORD<epochMillis> if omitted.', schema: { type: 'string', example: 'ORD1748340000000' } },
          { name: 'AMOUNT', required: true, description: 'Transaction amount in fils (100 AED = 10000 fils)', schema: { type: 'string', example: '10000' } },
          { name: 'CURRENCY_CODE', required: true,fixed: true, description: 'ISO 4217 numeric currency code (e.g. "784" for AED, "356" for INR)', schema: { type: 'string', example: '784' } },
          { name: 'TXNTYPE', required: true, fixed: true, description: 'Transaction Type', schema: { type: 'string', example: 'SALE' } },
          { name: 'MOP_TYPE', required: true, fixed: true, description: 'Mode of Payment', schema: { type: 'string', example: 'PAYQR' } },
          { name: 'PAYMENT_TYPE', required: true, fixed: true, description: 'Payment Type ', schema: { type: 'string', example: 'PTQR' } },
          { name: 'CUST_EMAIL', required: true, description: 'Customer email address (valid format)', schema: { type: 'string', example: 'test@gmail.com' } },
          { name: 'CUST_PHONE', required: true, description: 'Customer phone number with country code', schema: { type: 'string', example: '971501234567' } },
          { name: 'PAYMENTS_REGION', required: true, fixed: true, description: 'Region of Payment ', schema: { type: 'string', example: 'DOMESTIC' } },
          { name: 'CARD_HOLDER_TYPE', required: true, fixed: true, description: 'Type of Card holder ', schema: { type: 'string', example: 'CONSUMER' } },
          { name: 'PAYER_ADDRESS', required: false, description: 'Payer mobile number. When present the call becomes a Request-to-Pay (push notification); when absent a Dynamic QR is generated.', schema: { type: 'string', example: '971501234567' } },
          { name: 'RETURN_URL', required: false, description: 'Deeplink redirection URL (used only in the deeplink flow)', schema: { type: 'string', example: 'https://merchant.example.com/payment-callback' } },
          { name: 'TERMINAL_ID', required: false, description: 'Device terminal identifier', schema: { type: 'string', example: 'T1' } },
        ],
        responses: [
          {
            status: '200',
            description: 'Decrypted Pay10 PG response — merged fields from Dynamic QR and Request to Pay flows',
            content: {
              RESPONSE_CODE: '000',
              PAYER_NAME: 'jon',
              TXN_ID: '2520124608299094',
              SURCHARGE_FLAG: 'N',
              HASH: '2D401230BFDEA2F0D9CCE58E153C17BD8B2093428FB60F5715C2BFE7219A6172',
              PAYMENT_TYPE: 'PTQR',
              STATUS: 'Sent to Bank',
              PAY_ID: '12345678',
              PAYER_ADDRESS: '123456789',
              QRSTRING: 'iVBORw0KGgoAAAANSUhEUgAAASwAAAEsAQAAAABRBrPYAAAKy0lEQVR42u2aMZLkLLaFwYEtgCNtTTjSFsBByEFbAEfaGjiSOyY4MCd/p+oZLyKZifGqKrojqvLLFoJ7zz1H2aR/8/Uv8of9YX/YH/ZfYoXM18ofsTvazyD7',
              ORDER_ID: '20250620_164607',
              AMOUNT: '10000',
              TOTAL_AMOUNT: '10000',
              RESPONSE_MESSAGE: 'Request Accepted',
              CUST_EMAIL: 'user@example.com',
              DEEPLINK_STRING: 'https://testuatapi.pay10.asia/deeplink/pay',
              CURRENCY_CODE: '784',
              CUST_PHONE: '123456789',
              EXPIRY: '1751547990',
              TERMINAL_ID: '123',
              MOP_TYPE: 'PAYQR',
              TXNTYPE: 'SALE',
              PG_REF_NUM: '1234567890'
            },
            responseFields: [
              { name: 'RESPONSE_CODE', type: 'string', description: 'Response code from Pay10 (000 or 026 indicates success, any other value indicates failure)' },
              { name: 'PAYER_NAME', type: 'string', description: 'Name of the payer' },
              { name: 'TXN_ID', type: 'string', description: 'Transaction ID generated by Pay10' },
              { name: 'SURCHARGE_FLAG', type: 'string', description: 'Indicates if surcharge is applied (Y/N)' },
              { name: 'HASH', type: 'string', description: 'SHA-256 hash value of the request payload' },
              { name: 'PAYMENT_TYPE', type: 'string', description: 'Payment type (PTQR)' },
              { name: 'STATUS', type: 'string', description: 'Payment status (e.g. "Sent to Bank")' },
              { name: 'PAY_ID', type: 'string', description: 'Unique identifier provided by Pay10' },
              { name: 'PAYER_ADDRESS', type: 'string', description: 'Mobile number of the payer — determines the flow (absent = Dynamic QR, present = RTP)' },
              { name: 'QRSTRING', type: 'string', description: 'Byte-encoded string to generate QR code — present in Dynamic QR flow only (no PAYER_ADDRESS)' },
              { name: 'ORDER_ID', type: 'string', description: 'Order ID generated by merchant' },
              { name: 'AMOUNT', type: 'string', description: 'Transaction amount in fils' },
              { name: 'TOTAL_AMOUNT', type: 'string', description: 'Transaction amount including fee and service charges' },
              { name: 'RESPONSE_MESSAGE', type: 'string', description: 'Response message from Pay10' },
              { name: 'CUST_EMAIL', type: 'string', description: 'User email ID echoed from the request' },
              { name: 'DEEPLINK_STRING', type: 'string', description: 'Deep link to open Pay10 app — present in Dynamic QR flow only (no PAYER_ADDRESS)' },
              { name: 'CURRENCY_CODE', type: 'string', description: 'Currency code (784 = AED)' },
              { name: 'CUST_PHONE', type: 'string', description: 'Customer phone number passed in request' },
              { name: 'EXPIRY', type: 'string', description: 'Expiry time in milliseconds after epoch — present in Dynamic QR flow only (no PAYER_ADDRESS)' },
              { name: 'TERMINAL_ID', type: 'string', description: 'Terminal ID passed in request' },
              { name: 'MOP_TYPE', type: 'string', description: 'Mode of payment (PAYQR)' },
              { name: 'TXNTYPE', type: 'string', description: 'Transaction type (SALE)' },
              { name: 'PG_REF_NUM', type: 'string', description: 'Payment gateway reference number' }
            ]
          }
        ],
        successResponse: {
          status: '200',
          description: 'Dynamic QR generated / RTP push accepted',
          contentType: 'JSON',
          content: {
            RESPONSE_CODE: '000',
            PAYER_NAME: 'jon',
            TXN_ID: '2520124608299094',
            STATUS: 'Sent to Bank',
            PAY_ID: '12345678',
            ORDER_ID: '20250620_164607',
            AMOUNT: '10000',
            RESPONSE_MESSAGE: 'Request Accepted',
            DEEPLINK_STRING: 'https://testuatapi.pay10.asia/deeplink/pay',
            EXPIRY: '1751547990',
            QRSTRING: 'iVBORw0KGgoAAAANSUhEUgAAASwAAAEsAQAAAABRBrPYAAAKy0lEQVR42u2aMZLkLLaFwYEtgCNtTTjSFsBByEFbAEfaGjiSOyY4MCd/p+oZLyKZifGqKrojqvLLFoJ7zz1H2aR/8/Uv8of9YX/YH/ZfYoXM18ofsTvazyD7'
          }
        },
        failureResponse: {
          status: '400',
          description: 'Validation error',
          contentType: 'JSON',
          content: {
            RESPONSE_CODE: '319',
            PAYER_NAME: 'NA',
            TXN_ID: '2620115738444080',
            CUST_PHONE: '123456789',
            MOP_TYPE: 'PAYQR',
            TXNTYPE: 'SALE',
            CURRENCY_CODE: '123',
            HASH: 'BDB548D0C4806D7CFC57E738CEE5B192834AF1CBE32ED3D702852AE5E8A26FF7',
            PAYMENT_TYPE: 'PTQR',
            PG_TXN_MESSAGE: 'invalid Currency Code',
            STATUS: 'Invalid',
            PAY_ID: '66220289',
            PG_REF_NUM: '2620115738444080',
            PAYER_ADDRESS: 'NA',
            ORDER_ID: '20260720_115737',
            AMOUNT: '10000',
            RESPONSE_MESSAGE: 'Invalid Request',
            CUST_EMAIL: 'test@gmail.com'
          }
        }
      }
    ]
  },

  'status-enquiry': {
    title: 'Transaction Status Enquiry',
    version: '1.0',
    serverUrl: '',
    schemas: {},
    endpoints: [
      {
        isFromPG: true,
        // path: '/developer-portal/payment-gateway/transaction-status',
        path: '/pgws/transact',
        method: 'POST',
        tag: 'Payment Gateway',
        title: 'Transaction Status Enquiry',
        description: 'Fetch the current status of a previously-initiated transaction. Wraps Pay10 PG /pgws/transact. Unlike validate-mobile and collect-pay this endpoint is not AES-encrypted — payload is plain JSON signed with a SHA-256 HASH and the response is plain JSON.',
        operationId: 'transactionStatus',
        parameters: [
          // { name: 'Authorization', in: 'header', required: true, description: 'Bearer access token', schema: { type: 'string', example: 'Bearer eyJhbGciOiJIUzI1NiJ9' } }
        ],
        requestBody: null,
        requestBodyFields: [
          { name: 'PAY_ID', required: true, fixed: true, description: 'Unique identifier provided by Pay10', schema: { type: 'string', example: '312432' } },
          { name: 'ORDER_ID', required: true, description: 'Merchant reference number of the original transaction to look up', schema: { type: 'string', example: 'ORD1748340000000' } },
          { name: 'AMOUNT', required: true, description: 'Original transaction amount in fils (must match the original)', schema: { type: 'string', example: '10000' } },
          { name: 'CURRENCY_CODE', required: true, fixed: true, description: 'ISO numeric currency code of the original transaction (784 = AED)', schema: { type: 'string', example: '784' } },
          { name: 'TXNTYPE', required: true, fixed: true, description: 'Type of Transaction ', schema: { type: 'string', example: 'STATUS ' } }
        ],
        responses: [
          {
            status: '200',
            description: 'Pay10 PG transaction status — flat key-value object',
            content: {
              PAY_ID: '160234578452178',
              RRN: '789456132',
              TXN_ID: '150611417421130',
              ORDER_ID: 'ESN78452',
              IS_STATUS_FINAL: 'Y',
              ORIG_TXN_ID: '4809120328094623',
              HASH: '7995156CE4C40C44C41BECA3',
              CUST_PHONE: '09999999999',
              CUST_EMAIL: 'user@example.com',
              AMOUNT: '100',
              CURRENCY_CODE: '826',
              MOP_TYPE: 'PAYQR',
              PAYMENT_TYPE: 'PTQR',
              PG_REF_NUM: '1234567890',
              PG_TXN_MESSAGE: 'SUCCESS',
              RESPONSE_CODE: '000',
              RESPONSE_DATE_TIME: '2022-03-28 13:01:48',
              RESPONSE_MESSAGE: 'SUCCESS',
              SURCHARGE_FLAG: 'Y',
              STATUS: 'Approved/Captured/Declined/Failed',
              TOTAL_AMOUNT: '10134',
              TXNTYPE: 'SALE/AUTH'
            },
            responseFields: [
              { name: 'PAY_ID', type: 'string', description: 'Merchant ID provided by Pay10' },
              { name: 'RRN', type: 'string', description: 'Bank reference number' },
              { name: 'TXN_ID', type: 'string', description: 'Transaction ID generated by Pay10 to identify the current step' },
              { name: 'ORDER_ID', type: 'string', description: 'Merchant reference number' },
              { name: 'IS_STATUS_FINAL', type: 'string', description: 'Indicates if the transaction status is final (Y/N)' },
              { name: 'ORIG_TXN_ID', type: 'string', description: 'Original transaction reference number' },
              { name: 'HASH', type: 'string', description: 'Unique value generated by SHA-256 hashing algorithm' },
              { name: 'CUST_PHONE', type: 'string', description: 'Customer phone number' },
              { name: 'CUST_EMAIL', type: 'string', description: 'Customer email address' },
              { name: 'AMOUNT', type: 'string', description: 'Total sale amount' },
              { name: 'CURRENCY_CODE', type: 'string', description: '3-digit ISO currency code (e.g. 784 = AED, 826 = GBP)' },
              { name: 'MOP_TYPE', type: 'string', description: 'Method of payment (PAYQR)' },
              { name: 'PAYMENT_TYPE', type: 'string', description: 'Type of payment mode used by customer (PTQR)' },
              { name: 'PG_REF_NUM', type: 'string', description: 'Payment gateway reference number' },
              { name: 'PG_TXN_MESSAGE', type: 'string', description: 'Detailed message for the transaction' },
              { name: 'RESPONSE_CODE', type: 'string', description: 'Response code from Pay10 (000 or 026 indicates success, any other value indicates failure)' },
              { name: 'RESPONSE_DATE_TIME', type: 'string', description: 'Response date and time' },
              { name: 'RESPONSE_MESSAGE', type: 'string', description: 'Response message for transaction status' },
              { name: 'SURCHARGE_FLAG', type: 'string', description: 'Indicates if surcharge is enabled (Y/N)' },
              { name: 'STATUS', type: 'string', description: 'Transaction status (Approved / Captured / Declined / Failed)' },
              { name: 'TOTAL_AMOUNT', type: 'string', description: 'Total amount debited to customer including surcharge' },
              { name: 'TXNTYPE', type: 'string', description: 'Type of transaction processed (SALE / AUTH)' }
            ]
          }
        ],
        successResponse: {
          status: '200',
          description: 'Status retrieved successfully',
          contentType: 'JSON',
          content: {
            PAY_ID: '160234578452178',
            RRN: '789456132',
            TXN_ID: '150611417421130',
            ORDER_ID: 'ESN78452',
            IS_STATUS_FINAL: 'Y',
            ORIG_TXN_ID: '4809120328094623',
            HASH: '7995156CE4C40C44C41BECA3',
            CUST_PHONE: '09999999999',
            CUST_EMAIL: 'user@example.com',
            AMOUNT: '100',
            CURRENCY_CODE: '826',
            MOP_TYPE: 'PAYQR',
            PAYMENT_TYPE: 'PTQR',
            PG_REF_NUM: '1234567890',
            PG_TXN_MESSAGE: 'SUCCESS',
            RESPONSE_CODE: '000',
            RESPONSE_DATE_TIME: '2022-03-28 13:01:48',
            RESPONSE_MESSAGE: 'SUCCESS',
            SURCHARGE_FLAG: 'Y',
            STATUS: 'Approved/Captured/Declined/Failed',
            TOTAL_AMOUNT: '10134',
            TXNTYPE: 'SALE/AUTH'
          }
        },
        failureResponse: {
          status: '400',
          description: 'Validation error',
          contentType: 'JSON',
          content: {
            RESPONSE_DATE_TIME: "2026-07-20 12:09:29",
            RESPONSE_CODE: "300",
            STATUS: "Invalid",
            PAY_ID: "66220289",
            ORDER_ID: "20260720_115737",
            AMOUNT: "1000",
            RESPONSE_MESSAGE: "Invalid currency_code",
            TXNTYPE: "STATUS",
            CURRENCY_CODE: "123",
            HASH: "407FC4169DF99C5E304776A46DD17F7315077326CAC4CF0C26C52C10FD973C1A"
          }
        }
      }
    ]
  },

  'webhook': {
    title: 'Webhook (Sale/Refund)',
    version: '1.0',
    serverUrl: '',
    note: {
      content: 'Note: To test Sale and Refund webhooks, please configure your webhook callback URL in the webhook settings. All webhook notifications will be sent to the configured endpoint during testing.',
      'background-color': '#EEF2FF',
      'text-color': '#3730A3',
      'border-color': '#6366F1',
    },
    schemas: {},
    endpoints: [
      {
        isFromPG: true,
        path: '',
        method: '',
        tag: 'Webhook Sale/Refund',
        title: 'Transaction Status Enquiry',
        description: 'We send webhook when there is any update on transaction status. Merchant should provide us the URL for webhook callbacks, We expect http response code from 200 to 299 in response. If the response code is not within this range, we retry after 15 mins for maximum of 3 times.',
        operationId: 'webhook',
        parameters: [
          { name: 'Authorization', in: 'header', required: true, description: 'Bearer access token', schema: { type: 'string', example: 'Bearer eyJhbGciOiJIUzI1NiJ9' } }
        ],
        requestBody: null,
        requestBodyFields: [],
        responses: [
          {
            status: '200',
            description: 'Webhook payload sent by Pay10 to the merchant callback URL for transaction status updates',
            content: {
              PG_REF_NUM: '2624110532023023',
              TXN_ID: '2601143700066659',
              RRN: '2601142524215562',
              ORDER_ID: '20260624_110531',
              REFUND_ORDER_ID: '20260624_110532',
              TXNTYPE: 'REFUND',
              CUST_NAME: null,
              CUST_EMAIL: 'ckkadam1999@gmail.com',
              STATUS: 'Captured',
              PAYMENT_TYPE: 'PTQR',
              MOP_TYPE: 'PAYQR',
              AMOUNT: '1.00',
              TOTAL_AMOUNT: '100.00',
              CREATE_DATE: '2026-07-01 14:37:07',
              UPDATE_DATE: '2026-07-01 14:37:07',
              PAY_ID: '12345678',
              HASH: 'AEEF001614AAAF44724FB7039B9C9170B2E48EDDC37BD0DB824931177C7914DB'
            },
            responseFields: [
              { name: 'PAY_ID', type: 'string', description: 'Merchant ID provided by Pay10' },
              { name: 'TXN_ID', type: 'string', description: 'Transaction ID generated by Pay10' },
              { name: 'ORDER_ID', type: 'string', description: 'Merchant reference number' },
              { name: 'REFUND_ORDER_ID', type: 'string', description: 'Refund order ID' },
              { name: 'RRN', type: 'string', description: 'Bank reference number' },
              { name: 'HASH', type: 'string', description: 'Unique value generated by SHA 256 hashing algorithm' },
              { name: 'CUST_NAME', type: 'string', description: 'Customer Name' },
              { name: 'CUST_EMAIL', type: 'string', description: 'Customer email' },
              { name: 'AMOUNT', type: 'string', description: 'Total sale amount' },
              { name: 'MOP_TYPE', type: 'string', description: 'Method of payment' },
              { name: 'PAYMENT_TYPE', type: 'string', description: 'Type of payment mode used by customer' },
              { name: 'PG_REF_NUM', type: 'string', description: 'Payment gateway reference number' },
              { name: 'STATUS', type: 'string', description: 'Transaction status' },
              { name: 'TOTAL_AMOUNT', type: 'string', description: 'Total amount debited to customer' },
              { name: 'TXNTYPE', type: 'string', description: 'Type of transaction processed (SALE / REFUND)' },
              { name: 'CREATE_DATE', type: 'string', description: 'Transaction creation date/time (yyyy-MM-dd HH:mm:ss)' },
              { name: 'UPDATE_DATE', type: 'string', description: 'Transaction last updated date/time (yyyy-MM-dd HH:mm:ss)' }
            ]
          }
        ],
        successResponse: {
          status: '200',
          description: 'Webhook payload received successfully',
          contentType: 'JSON',
          content: {
            PG_REF_NUM: "2624110532023023",
            TXN_ID: "2601143700066659",
            RRN: "2601142524215562",
            ORDER_ID: "20260624_110531",
            REFUND_ORDER_ID: "20260624_110532",
            TXNTYPE: "REFUND",
            CUST_NAME: "null",
            CUST_EMAIL: "ckkadam1999@gmail.com",
            STATUS: "Captured",
            PAYMENT_TYPE: "PTQR",
            MOP_TYPE: "PAYQR",
            AMOUNT: "1.00",
            TOTAL_AMOUNT: "100.00",
            CREATE_DATE: "2026-07-01 14:37:07",
            UPDATE_DATE: "2026-07-01 14:37:07",
            PAY_ID: "12345678",
            HASH: "AEEF001614AAAF44724FB7039B9C9170B2E48EDDC37BD0DB824931177C7914DB"
          }
        },
        failureResponse: {
          status: '400',
          description: 'Client authentication failed',
          contentType: 'JSON',
          content: {
            PG_REF_NUM: "2624110532023023",
            TXN_ID: "2601143700066659",
            RRN: "2601142524215562",
            ORDER_ID: "20260624_110531",
            REFUND_ORDER_ID: "20260624_110532",
            TXNTYPE: "REFUND",
            CUST_NAME: "null",
            CUST_EMAIL: "ckkadam1999@gmail.com",
            STATUS: "Failed",
            PAYMENT_TYPE: "PTQR",
            MOP_TYPE: "PAYQR",
            AMOUNT: "25.00",
            TOTAL_AMOUNT: "100.00",
            CREATE_DATE: "2026-07-01 14:37:07",
            UPDATE_DATE: "2026-07-01 14:37:07",
            PAY_ID: "12345678",
            HASH: "B8D951C057AA721379DF38FCF3B5425DD9C5885431A070CF1D2839916A783CBA"
          }
        }
      }
    ]
  },

  'refund-status': {
    title: 'Refund Status ',
    version: '1.0',
    serverUrl: '',
    schemas: {},
    endpoints: [
      {
        // path: '/developer-portal/payment-gateway/refund-status',
        isFromPG: true,
        path: '/pgws/refund/requery',
        method: 'POST',
        tag: 'refundStatus ',
        title: 'Refund Status ',
        description: 'The API is to be used to fetch/check the current status of the transaction.',
        operationId: 'validatePhoneNumber',
        parameters: [
          // { name: 'Authorization', in: 'header', required: true, description: 'Bearer access token', schema: { type: 'string', example: 'Bearer eyJhbGciOiJIUzI1NiJ9' } }
        ],
        requestBody: null,
        requestBodyFields: [
          { name: 'PAY_ID', required: true, fixed: true, description: 'Unique identifier provided by Pay10', schema: { type: 'string', example: '312432' } },
          { name: 'REFUND_ORDER_ID', required: true, description: 'Merchant reference number for refund ', schema: { type: 'string', example: 'ESN78452' } },
          { name: 'ORDER_ID', required: true, description: 'Merchant reference number', schema: { type: 'string', example: 'ESN78452' } }
        ],
        responses: [
          {
            status: '200',
            description: 'Phone number validated successfully',
            content: {
              PAY_ID: "160234578452178", RRN: "789456132", ORDER_ID: "ESN78452", REFUND_ORDER_ID: "ESN78452", AMOUNT: "100", MOP_TYPE: "PAYQR", PAYMENT_TYPE: "PTQR", PG_REF_NUM: "1234567890 ", RESPONSE_CODE: "000", RESPONSE_DATE_TIME: "2022-03-28 13:01:48 ", SURCHARGE_FLAG: "Y", REFUND_FLAG: "R ", STATUS: "Approved/Captured/Declined/Failed ", TXNTYPE: "REFUND "
            },
            responseFields: [
              { name: 'PAY_ID', type: 'NUMERIC', description: 'Merchant Id provided by Pay10 ' },
              { name: 'RRN', type: 'string', description: 'Bank reference number ' },
              { name: 'ORDER_ID', type: 'string', description: 'Merchant reference number ' },
              { name: 'REFUND_ORDER_ID', type: 'string', description: 'Merchant reference number for refund ' },
              { name: 'AMOUNT', type: 'string', description: 'Total Sale Amount ' },
              { name: 'MOP_TYPE', type: 'string', description: 'Method of payment ' },
              { name: 'PAYMENT_TYPE', type: 'string', description: 'Type of payment mode used by customer  ' },
              { name: 'PG_REF_NUM', type: 'string', description: 'Payment gateway reference number  ' },
              { name: 'RESPONSE_CODE', type: 'string', description: 'Response code from Pay10 (000 or 026 indicates success, any other value indicates failure) ' },
              { name: 'RESPONSE_DATE_TIME', type: 'string', description: 'Date and time of transaction response ' },
              { name: 'SURCHARGE_FLAG', type: 'string', description: 'Flag indicating if surcharge was applied ' },
              { name: 'REFUND_FLAG', type: 'string', description: 'Refund flag is “R” ' },
              { name: 'STATUS', type: 'string', description: 'Current status of the transaction ' },
              { name: 'TXNTYPE', type: 'string', description: 'Type of transaction ' },
            ]
          }
        ],
        successResponse: {
          status: '200',
          description: 'Phone number validated successfully',
          contentType: 'JSON',
          content: {
            PAY_ID: "160234578452178", RRN: "789456132", ORDER_ID: "ESN78452", REFUND_ORDER_ID: "ESN78452", AMOUNT: "100", MOP_TYPE: "PAYQR", PAYMENT_TYPE: "PTQR", PG_REF_NUM: "1234567890 ", RESPONSE_CODE: "000", RESPONSE_DATE_TIME: "2022-03-28 13:01:48 ", SURCHARGE_FLAG: "Y", REFUND_FLAG: "R ", STATUS: "Approved/Captured/Declined/Failed ", TXNTYPE: "REFUND "
          }
        },
        failureResponse: {
          status: '400',
          description: 'Invalid request payload',
          contentType: 'JSON',
          content: {
            RESPONSE_DATE_TIME: "2026-07-20 12:09:29",
            RESPONSE_CODE: "300",
            STATUS: "Invalid",
            PAY_ID: "66220289",
            ORDER_ID: "20260720_115737",
            AMOUNT: "1000",
            RESPONSE_MESSAGE: "Invalid currency_code",
            TXNTYPE: "STATUS",
            CURRENCY_CODE: "123",
            HASH: "407FC4169DF99C5E304776A46DD17F7315077326CAC4CF0C26C52C10FD973C1A"
          }

        }
      }
    ]
  },

  'refund-initiation': {
    title: 'Refund Transaction',
    version: '1.0',
    serverUrl: '',
    schemas: {},
    endpoints: [
      {
        isFromPG: true,
        // path: '/developer-portal/payment-gateway/refund',
        path: '/pgws/merchant/refund',
        method: 'POST',
        tag: 'Payment Gateway',
        title: 'Refund Transaction',
        description: 'This endpoint initiates the refund request from the merchant. This API requires encryption. Encryption logic is mentioned below in the document.',
        operationId: 'refundInitiation',
        parameters: [
          // { name: 'Authorization', in: 'header', required: true, description: 'Bearer access token', schema: { type: 'string', example: 'Bearer eyJhbGciOiJIUzI1NiJ9' } }
        ],
        requestBody: null,
        requestBodyFields: [
          { name: 'PAY_ID', required: true, fixed: true, description: 'Unique identifier provided by Pay10', schema: { type: 'string', example: '312432' } },
          { name: 'ORDER_ID', required: true, description: 'Order id generated by merchant', schema: { type: 'string', example: '20260624_110531' } },
          { name: 'AMOUNT', required: true, description: 'Refund Amount in Fils', schema: { type: 'string', example: '100' } },
          { name: 'CURRENCY_CODE', required: true, fixed: true, description: 'Currency code', schema: { type: 'string', example: '784' } },
          { name: 'TXNTYPE', required: true, fixed: true, description: 'Transaction Type', schema: { type: 'string', example: 'REFUND' } },
          { name: 'PG_REF_NUM', required: true, description: 'Payment gateway reference number', schema: { type: 'string', example: '2624110532023023' } },
          { name: 'REFUND_FLAG', required: true, fixed: true, description: 'Refund flag is "R"', schema: { type: 'string', example: 'R' } },
          { name: 'REFUND_ORDER_ID', required: true, description: 'Merchant Reference Number for Refund', schema: { type: 'string', example: 'DB1782289329599613' } }
        ],
        responses: [
          {
            status: '200',
            description: 'Refund initiated successfully',
            content: {
              RESPONSE_CODE: '000',
              RRN: '1094760624110533',
              STATUS: 'REFUND_INITIATED',
              AMOUNT: '10000',
              CUST_EMAIL: 'user@example.com',
              REFUND_ORDER_ID: 'REF123456789',
              TXNTYPE: 'REFUND',
              TXN_ID: '2520124608299094',
              PAYMENT_TYPE: 'PTQR',
              TOTAL_AMOUNT: '10000',
              REFUND_FLAG: 'R',
              CUST_PHONE: '123456789',
              MOP_TYPE: 'PAYQR',
              CURRENCY_CODE: '784',
              PG_REF_NUM: '1234567890',
              SALE_AMOUNT: '10000',
              PAY_ID: '12345678',
              ORDER_ID: '20250620_164607'
            },
            responseFields: [
              { name: 'RESPONSE_CODE', type: 'string', description: 'Response code from Pay10 (000 or 026 indicates success, any other value indicates failure)' },
              { name: 'RRN', type: 'string', description: 'Bank reference number' },
              { name: 'STATUS', type: 'string', description: 'Payment status' },
              { name: 'AMOUNT', type: 'string', description: 'Refund Amount in Fils' },
              { name: 'CUST_EMAIL', type: 'string', description: 'User email ID' },
              { name: 'REFUND_ORDER_ID', type: 'string', description: 'Merchant Reference Number for Refund' },
              { name: 'TXNTYPE', type: 'string', description: 'Transaction Type' },
              { name: 'TXN_ID', type: 'string', description: 'Transaction ID generated by pay10' },
              { name: 'PAYMENT_TYPE', type: 'string', description: 'Payment Type' },
              { name: 'TOTAL_AMOUNT', type: 'string', description: 'Total amount of original transaction' },
              { name: 'REFUND_FLAG', type: 'string', description: 'Refund flag is "R"' },
              { name: 'CUST_PHONE', type: 'string', description: 'Customer phone passed in request' },
              { name: 'MOP_TYPE', type: 'string', description: 'Mode of Payment' },
              { name: 'CURRENCY_CODE', type: 'string', description: 'Currency code' },
              { name: 'PG_REF_NUM', type: 'string', description: 'Payment gateway reference number' },
              { name: 'SALE_AMOUNT', type: 'string', description: 'Sale amount of original transaction' },
              { name: 'PAY_ID', type: 'string', description: 'Unique Identifier provided by Pay10' },
              { name: 'ORDER_ID', type: 'string', description: 'Order id generated by merchant' }
            ]
          }
        ],
        successResponse: {
          status: '200',
          description: 'Refund initiated successfully',
          contentType: 'JSON',
          content: {
            RESPONSE_CODE: '000',
            RRN: '1094760624110533',
            STATUS: 'REFUND_INITIATED',
            AMOUNT: '10000',
            CUST_EMAIL: 'user@example.com',
            REFUND_ORDER_ID: 'REF123456789',
            TXNTYPE: 'REFUND',
            TXN_ID: '2520124608299094',
            PAYMENT_TYPE: 'PTQR',
            TOTAL_AMOUNT: '10000',
            REFUND_FLAG: 'R',
            CUST_PHONE: '123456789',
            MOP_TYPE: 'PAYQR',
            CURRENCY_CODE: '784',
            PG_REF_NUM: '1234567890',
            SALE_AMOUNT: '10000',
            PAY_ID: '12345678',
            ORDER_ID: '20250620_164607'
          }
        },
        failureResponse: {
          status: '400',
          description: 'Invalid request payload',
          contentType: 'JSON',
          content: {
            RRN: '1094760624110533',
            STATUS: 'Declined',
            AMOUNT: '100',
            CUST_EMAIL: 'ckkadam1999@gmail.com',
            REFUND_ORDER_ID: 'DB1782289329599613',
            TXNTYPE: 'REFUND',
            SURCHARGE_FLAG: 'N',
            PAYMENT_TYPE: 'PTQR',
            TOTAL_AMOUNT: '10000',
            REFUND_FLAG: 'R',
            RESPONSE_CODE: '004',
            CUST_PHONE: '768355253',
            MOP_TYPE: 'PAYQR',
            CURRENCY_CODE: '784',
            PG_REF_NUM: '2624110532023023',
            SALE_AMOUNT: '10000',
            PAY_ID: '66220289',
            ORDER_ID: '20260624_110531'
          }
        }
      }
    ]
  },


  // Payout module — Web Portal and Mobile API channels (see api-explorer.payout.config.ts)
  ...PAYOUT_API_CONFIGS,
};
